import * as React from "react";

/**
 * Purely presentational user preferences.
 *
 * Deliberately kept in localStorage: this is not domain state, losing it on a
 * device change is harmless, and it does not need to be held server-side.
 * Everything that substantiates the proof of concept (conversations, feedback,
 * times) lives in the database instead.
 */

const PREFIX = "nl-coach:";
const PREFERENCE_EVENT = "nl-preference-change";
const sessionValues = new Map<string, string>();

function usePersistentValue(key: string, fallback: string) {
  const storageKey = `${PREFIX}${key}`;

  const [value, setValue] = React.useState<string>(() => {
    try {
      return (
        sessionValues.get(storageKey) ??
        window.localStorage.getItem(storageKey) ??
        fallback
      );
    } catch {
      return fallback;
    }
  });

  React.useEffect(() => {
    const sync = (event: Event) => {
      if (event instanceof StorageEvent) {
        if (event.key !== storageKey && event.key !== null) return;
        sessionValues.delete(storageKey);
        setValue(window.localStorage.getItem(storageKey) ?? fallback);
      } else {
        const change = (event as CustomEvent<{ key: string; value: string }>)
          .detail;
        if (change.key === storageKey) setValue(change.value);
      }
    };
    window.addEventListener(PREFERENCE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(PREFERENCE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [storageKey, fallback]);

  const update = React.useCallback(
    (next: string) => {
      setValue(next);
      sessionValues.set(storageKey, next);
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        // Private mode or blocked storage: the preference then applies to this
        // session only, which is acceptable.
      }
      window.dispatchEvent(
        new CustomEvent(PREFERENCE_EVENT, {
          detail: { key: storageKey, value: next },
        }),
      );
    },
    [storageKey],
  );

  return [value, update] as const;
}

/** Whether persona replies are read aloud automatically. */
export function useAutoSpeak() {
  const [raw, setRaw] = usePersistentValue("auto-speak", "0");
  const set = React.useCallback(
    (enabled: boolean) => setRaw(enabled ? "1" : "0"),
    [setRaw],
  );
  return [raw === "1", set] as const;
}

const MICROPHONE_KEY = "microphone-id";

/**
 * Which microphone the advisor picked, or null for the system default.
 *
 * Device-local on purpose, even though it is an account setting in spirit: a
 * `deviceId` is salted per origin and browser profile, so the same headset has a
 * different id on the advisor's next machine. Stored server-side it would come
 * back as a dangling reference.
 */
export function useMicrophoneId() {
  const [raw, setRaw] = usePersistentValue(MICROPHONE_KEY, "");
  const set = React.useCallback(
    (deviceId: string | null) => setRaw(deviceId ?? ""),
    [setRaw],
  );
  return [raw || null, set] as const;
}

/** Same value for the capture paths, which run outside the React tree. */
export function readMicrophoneId(): string | null {
  try {
    return (
      (sessionValues.get(`${PREFIX}${MICROPHONE_KEY}`) ??
        window.localStorage.getItem(`${PREFIX}${MICROPHONE_KEY}`)) ||
      null
    );
  } catch {
    return null;
  }
}

/**
 * Which kind of one-time password the manager issued last. Remembered so that
 * walking through a workshop group keeps the readable variant for every row
 * instead of re-issuing each password twice.
 */
export function useOneTimePasswordVariant() {
  const [raw, setRaw] = usePersistentValue("one-time-password-variant", "random");
  const value: "random" | "email" = raw === "email" ? "email" : "random";
  return [value, setRaw as (next: "random" | "email") => void] as const;
}

/**
 * Which points of a unit's checklist the advisor ticked off.
 *
 * A self-check, not a record: nothing is credited or evaluated from it, so it
 * stays on the device. Keyed by session so a reload keeps the ticks and the
 * next run starts empty.
 */
export function useChecklistenHaken(sessionId: string) {
  const [raw, setRaw] = usePersistentValue(
    `checkliste:${sessionId}`,
    "",
  );
  const haken = React.useMemo(
    () => new Set(raw ? raw.split(",") : []),
    [raw],
  );
  const umschalten = React.useCallback(
    (punkt: string) => {
      const next = new Set(haken);
      if (next.has(punkt)) next.delete(punkt);
      else next.add(punkt);
      setRaw([...next].join(","));
    },
    [haken, setRaw],
  );
  return [haken, umschalten] as const;
}
